import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { ConfiguracaoIbptResponse } from '../../responses/motor-fiscal/configuracao-ibpt.response';
import { TestarConexaoIbptResponse } from '../../responses/motor-fiscal/testar-conexao-ibpt.response';
import { SituacaoTabelaIbptResponse } from '../../responses/motor-fiscal/situacao-tabela-ibpt.response';
import { AlterarCredencialIbptRequest } from '../../requests/motor-fiscal/alterar-credencial-ibpt.request';
import { SalvarDestinatarioIbptRequest } from '../../requests/motor-fiscal/salvar-destinatario-ibpt.request';
import { ImportacaoTabelaIbptRequest } from '../../requests/motor-fiscal/importacao-tabela-ibpt.request';
import { ValidacaoImportacaoTabelaIbptResponse } from '../../responses/motor-fiscal/validacao-importacao-tabela-ibpt.response';
import { AplicacaoImportacaoTabelaIbptResponse } from '../../responses/motor-fiscal/aplicacao-importacao-tabela-ibpt.response';
import { DetalheExecucaoIbptResponse } from '../../responses/motor-fiscal/detalhe-execucao-ibpt.response';
import { ListaExecucaoIbptResponse } from '../../responses/motor-fiscal/lista-execucao-ibpt.response';
import { IbptOrigemExecucaoEnum } from '../../../enums/motor-fiscal/ibpt-origem-execucao.enum';
import { IbptResultadoExecucaoEnum } from '../../../enums/motor-fiscal/ibpt-resultado-execucao.enum';

@Injectable({
    providedIn: 'root',
})
export class TabelaIbptService extends CommonService {
    private readonly apiUrl: string = environment.urlApiMotorFiscal + 'TabelaIbptAdmin';

    constructor(private httpClient: HttpClient) {
        super();
    }

    /**
     * Dados do card "API do IBPT": CNPJ, final do token, status da conexão, auditoria e destinatários do alerta.
     */
    public obterConfiguracao(): Observable<ConfiguracaoIbptResponse> {
        return this.httpClient
            .get<ConfiguracaoIbptResponse>(`${this.apiUrl}/configuracao`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Consulta o item fixo de teste com o CNPJ e o token gravados. Não altera a tabela nem gera execução.
     */
    public testarConexao(): Observable<TestarConexaoIbptResponse> {
        return this.httpClient
            .post<TestarConexaoIbptResponse>(`${this.apiUrl}/configuracao/testar-conexao`, null, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Testa o CNPJ e o token no IBPT e grava só se o teste der certo. Em falha, errorDescription traz a mensagem
     * pronta da história (token vazio, CNPJ inválido, API sem resposta ou par recusado).
     */
    public alterarCredencial(request: AlterarCredencialIbptRequest): Observable<ReturnStruct> {
        return this.httpClient
            .put<ReturnStruct>(`${this.apiUrl}/configuracao/credencial`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Grava a lista completa de destinatários do alerta e a opção de avisar quando a API voltar a funcionar.
     */
    public salvarDestinatario(request: SalvarDestinatarioIbptRequest): Observable<ReturnStruct> {
        return this.httpClient
            .put<ReturnStruct>(`${this.apiUrl}/configuracao/destinatario`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Card "Situação da tabela": status, versão em uso, verificações, contadores, execução em andamento, aviso de
     * falha e a última execução concluída.
     */
    public obterSituacao(): Observable<SituacaoTabelaIbptResponse> {
        return this.httpClient
            .get<SituacaoTabelaIbptResponse>(`${this.apiUrl}/situacao`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * "Atualizar agora" e "Tentar novamente": coloca a atualização pela API na fila do worker do Motor Fiscal.
     */
    public solicitarAtualizacao(): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/execucao`, null, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * "Cancelar atualização": registra o pedido de cancelamento da execução pela API em andamento; o worker a encerra
     * como Cancelada e mantém o progresso para a próxima atualização.
     */
    public cancelarExecucao(idIbptExecucao: number): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/execucao/${idIbptExecucao}/cancelar`, null, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Card "Histórico de execuções": 5 por página, da mais recente para a mais antiga. Filtro nulo = sem filtro.
     */
    public listarExecucao(
        quantidadeDias: number | null,
        ibptOrigemExecucao: IbptOrigemExecucaoEnum | null,
        ibptResultadoExecucao: IbptResultadoExecucaoEnum | null,
        paginaAtual: number
    ): Observable<ListaExecucaoIbptResponse> {
        let params = new HttpParams();

        if (quantidadeDias !== null) {
            params = params.set('quantidadeDias', quantidadeDias.toString());
        }
        if (ibptOrigemExecucao !== null) {
            params = params.set('ibptOrigemExecucao', ibptOrigemExecucao.toString());
        }
        if (ibptResultadoExecucao !== null) {
            params = params.set('ibptResultadoExecucao', ibptResultadoExecucao.toString());
        }
        params = params.set('paginaAtual', paginaAtual.toString());

        return this.httpClient
            .get<ListaExecucaoIbptResponse>(`${this.apiUrl}/execucao`, {
                headers: this.addHeaderToken().headers,
                params: params,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * Diálogo "Detalhe da execução": resumo, tentativas (API) ou arquivos (planilha) e logs da execução.
     */
    public obterDetalheExecucao(idIbptExecucao: number): Observable<DetalheExecucaoIbptResponse> {
        return this.httpClient
            .get<DetalheExecucaoIbptResponse>(`${this.apiUrl}/execucao/${idIbptExecucao}`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Etapa "Conferir" da importação por planilha: valida os arquivos e calcula o impacto na tabela sem gravar nada.
     */
    public validarImportacao(request: ImportacaoTabelaIbptRequest): Observable<ValidacaoImportacaoTabelaIbptResponse> {
        return this.httpClient
            .post<ValidacaoImportacaoTabelaIbptResponse>(`${this.apiUrl}/importacao/validar`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * "Confirmar importação": revalida os arquivos e grava as linhas válidas em uma única transação. Devolve os
     * eventos HTTP (progresso do envio e, no fim, a resposta) para o diálogo mostrar o andamento.
     */
    public aplicarImportacao(request: ImportacaoTabelaIbptRequest): Observable<HttpEvent<AplicacaoImportacaoTabelaIbptResponse>> {
        return this.httpClient
            .post<AplicacaoImportacaoTabelaIbptResponse>(`${this.apiUrl}/importacao/aplicar`, request, {
                headers: this.addHeaderToken().headers,
                reportProgress: true,
                observe: 'events',
            })
            .pipe(catchError(this.handleError));
    }
}
